import { useState, useEffect, useCallback, useRef, useMemo, type ReactNode } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth, isAdmin } from '@/lib/auth-context';
import { toast } from '@/lib/toast';
import { AddDeckDialog } from '@/components/AddDeckDialog';
import { DecASketchPanel } from '@/components/DecASketch';
import { queue } from '@/lib/api';
import type { TaskData } from '@/lib/api';

// ─── Task helpers ────────────────────────────────────────────────────────────

function phaseIcon(phase: string, deckId?: string): ReactNode {
  if (deckId?.startsWith('das:')) {
    const dasIconMap: Record<string, string> = {
      queued: 'hourglass_top',
      das_build: 'draw',
      completed: 'check_circle',
      failed: 'cancel',
      cancelled: 'block',
    };
    return <span className="material-icons text-[18px] leading-none">{dasIconMap[phase] ?? 'draw'}</span>;
  }
  const iconMap: Record<string, string> = {
    queued: 'hourglass_top',
    running: 'sync',
    power_level: 'flash_on',
    optimization: 'auto_fix_high',
    primer: 'description',
    rate_limited: 'hourglass_top',
    tuning_assess: 'flash_on',
    tuning_tune: 'auto_fix_high',
    new_cards_fetch: 'download',
    new_cards_analyze: 'auto_awesome',
    completed: 'check_circle',
    failed: 'cancel',
    cancelled: 'block',
  };
  const icon = iconMap[phase] ?? 'hourglass_top';
  return <span className="material-icons text-[18px] leading-none">{icon}</span>;
}

function taskDestination(task: { deck_id?: string }): string {
  if (task.deck_id?.startsWith('das:')) {
    const runId = task.deck_id.slice(4);
    return `/?das_run=${runId}`;
  }
  return `/deck/${task.deck_id}`;
}

// ─── Component ───────────────────────────────────────────────────────────────

interface NavBarProps {
  tasks: Map<string, TaskData>;
  sseConnected: boolean;
}

export function NavBar({ tasks, sseConnected }: NavBarProps) {
  const { user, logout, hasFeature } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showAddDeck, setShowAddDeck] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [unreadCompletions, setUnreadCompletions] = useState(0);
  const [dasOpen, setDasOpen] = useState(false);
  const [dasRunId, setDasRunId] = useState<number | null>(null);
  const bellRef = useRef<HTMLDivElement>(null);
  const prevTasksRef = useRef<Map<string, TaskData>>(new Map());
  const canSketch = !!user && hasFeature('dec_a_sketch');

  const handleLogout = async () => {
    try {
      await logout();
      toast.info('Logged out');
      navigate('/login');
    } catch {
      toast.error('Logout failed');
    }
  };

  const navItems: { path?: string; label: string; action?: () => void }[] = [
    { path: '/', label: 'Home' },
    { path: '/decks', label: 'Decks' },
    { path: '/podracer', label: 'Podracer' },
    ...(hasFeature('elo') ? [{ path: '/leaderboard', label: 'Leaderboard' }] : []),
    ...(user ? [{ path: '/my-decks', label: 'My Decks' }] : []),
    ...(canSketch ? [{ label: 'Dec-a-Sketch', action: () => { setDrawerOpen(false); setDasRunId(null); setDasOpen(true); } }] : []),
    ...(user ? [{ path: '/settings', label: 'Settings' }] : []),
    ...(isAdmin(user) ? [{ path: '/admin', label: 'Admin' }] : []),
  ];

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // Close drawer on Escape key
  useEffect(() => {
    if (!drawerOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDrawer();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [drawerOpen, closeDrawer]);

  // Close bell dropdown on click outside or Escape
  useEffect(() => {
    if (!bellOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) {
        setBellOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setBellOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [bellOpen]);

  // Close bell and DaS drawer on navigation (skip DaS close if ?das_run is present)
  useEffect(() => {
    setBellOpen(false);
    if (!searchParams.has('das_run')) {
      setDasOpen(false);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open DaS panel when ?das_run param is present (from bell notification link).
  // Depends on searchParams so it re-fires after navigation (not just on mount).
  useEffect(() => {
    const runIdStr = searchParams.get('das_run');
    if (!runIdStr) return;
    const id = parseInt(runIdStr, 10);
    if (!isNaN(id)) {
      setDasRunId(id);
      setDasOpen(true);
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('das_run');
        return next;
      }, { replace: true });
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const userInitial = user?.display_name?.[0]?.toUpperCase()
    ?? user?.email?.[0]?.toUpperCase()
    ?? null;

  // Pod-ready state: tracks whether the user has an active lobby/confirmed queue
  const [podReady, setPodReady] = useState<{ lobbyId: string } | null>(null);

  // Poll queue status every 15s when logged in, has queue feature, and NOT on the Podracer page
  useEffect(() => {
    if (!user || !hasFeature('podracer_queue')) return;
    if (location.pathname === '/podracer') {
      // On the Podracer page, clear any stale pod-ready badge (they can see the queue directly)
      setPodReady(null);
      return;
    }

    async function checkQueueStatus() {
      try {
        const status = await queue.getStatus();
        if (status.status === 'lobby' && status.lobby?.lobby_id) {
          setPodReady({ lobbyId: status.lobby.lobby_id });
        } else {
          setPodReady(null);
        }
      } catch {
        // silently ignore — queue endpoint may not exist yet
      }
    }

    if (podReady) return;  // already notified — stop polling until they navigate to /podracer
    void checkQueueStatus();
    const interval = setInterval(() => { void checkQueueStatus(); }, 15_000);
    return () => clearInterval(interval);
  }, [user, hasFeature, location.pathname, podReady]);

  // Detect active→inactive transitions to track unread completions
  useEffect(() => {
    const prev = prevTasksRef.current;
    tasks.forEach((task, id) => {
      const prevTask = prev.get(id);
      if (prevTask?.is_active && !task.is_active) {
        setUnreadCompletions(c => c + 1);
      }
    });
    prevTasksRef.current = new Map(tasks);
  }, [tasks]);

  // Task data for bell
  const taskList = useMemo(() => Array.from(tasks.values()), [tasks]);
  const activeCount = taskList.filter(t => t.is_active).length;

  return (
    <>
      {/* Top nav bar */}
      <header
        className="bg-surface-container shadow-[0_2px_4px_rgba(0,0,0,0.3)]"
      >
        <div className="flex items-center gap-2 px-2 py-1 text-white">
          {/* Hamburger */}
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation"
            aria-expanded={drawerOpen}
            className="bg-transparent border-none text-on-surface cursor-pointer text-[24px] p-spacing-sm rounded-sm"
          >
            &#9776;
          </button>

          {/* Logo */}
          <Link to="/">
            <img
              src="/static/images/meta-command-logo.png"
              alt="MetaCommand"
              className="h-[56px] w-auto object-contain block"
            />
          </Link>

          <div className="flex-1" />

          {/* Desktop nav links */}
          <Link to="/decks" className="nav-text-btn desktop-nav-link text-on-surface no-underline text-md font-medium py-[6px] px-[12px] rounded-pill">
            Decks
          </Link>
          <Link to="/podracer" className="nav-text-btn desktop-nav-link text-on-surface no-underline text-md font-medium py-[6px] px-[12px] rounded-pill">
            Podracer
          </Link>
          {hasFeature('elo') && (
            <Link to="/leaderboard" className="nav-text-btn desktop-nav-link text-on-surface no-underline text-md font-medium py-[6px] px-[12px] rounded-pill">
              Leaderboard
            </Link>
          )}
          {user && (
            <Link to="/my-decks" className="nav-text-btn desktop-nav-link text-on-surface no-underline text-md font-medium py-[6px] px-[12px] rounded-pill">
              My Decks
            </Link>
          )}
          {canSketch && (
            <button
              type="button"
              onClick={() => { setDasOpen(prev => !prev); setDasRunId(null); }}
              className={`nav-text-btn desktop-nav-link text-md font-medium py-[6px] px-[12px] rounded-pill border-none cursor-pointer transition-colors ${dasOpen ? 'text-primary bg-primary/10' : 'text-on-surface bg-transparent'}`}
            >
              Dec-a-Sketch
            </button>
          )}

          {/* Add deck button */}
          {user && (
            <button
              type="button"
              onClick={() => setShowAddDeck(true)}
              aria-label="Add deck"
              title="Add deck"
              className="material-icons bg-transparent border-none text-primary cursor-pointer text-[22px] w-[36px] h-[36px] p-0 rounded-full flex items-center justify-center"
            >
              add
            </button>
          )}

          {/* Notification bell */}
          <div ref={bellRef} className="relative">
            <button
              type="button"
              onClick={() => { setBellOpen(prev => !prev); setUnreadCompletions(0); }}
              aria-label={`Notifications${activeCount > 0 ? ` (${activeCount} active)` : ''}${unreadCompletions > 0 ? ` (${unreadCompletions} completed)` : ''}${podReady ? ' (Pod ready)' : ''}`}
              title="Tasks"
              className="material-icons bg-transparent border-none text-on-surface cursor-pointer text-[22px] w-[36px] h-[36px] p-0 rounded-full flex items-center justify-center relative"
            >
              notifications
              {/* Primary badge dot for active analysis tasks */}
              {activeCount > 0 && (
                <span className="absolute top-[4px] right-[4px] w-[10px] h-[10px] rounded-full bg-primary border-2 border-surface-container" />
              )}
              {/* Green badge dot for unread completions (persists until tray is opened) */}
              {activeCount === 0 && unreadCompletions > 0 && (
                <span className="absolute top-[4px] right-[4px] w-[10px] h-[10px] rounded-full bg-green-500 border-2 border-surface-container" />
              )}
              {/* Amber badge dot when a pod is ready (shown only when no other badge) */}
              {podReady && activeCount === 0 && unreadCompletions === 0 && (
                <span className="absolute top-[4px] right-[4px] w-[10px] h-[10px] rounded-full bg-amber-400 border-2 border-surface-container" />
              )}
            </button>

            {/* Dropdown */}
            {bellOpen && (
              <div className="absolute right-0 top-full mt-1 w-[340px] max-h-[400px] overflow-y-auto rounded-md bg-surface-container-high border border-outline-variant shadow-[0_8px_24px_rgba(0,0,0,0.5)] z-[1300]">
                <div className="px-3 py-2 text-xs font-semibold text-on-surface-variant uppercase tracking-wider border-b border-outline-variant">
                  Tasks
                </div>

                {/* Pod-ready notification item */}
                {podReady && (
                  <button
                    type="button"
                    onClick={() => {
                      setBellOpen(false);
                      navigate(`/podracer?lobby=${podReady.lobbyId}`);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 bg-amber-500/10 border-none border-b border-outline-variant text-left cursor-pointer hover:bg-amber-500/20 transition-colors duration-100"
                  >
                    <span className="material-symbols-outlined text-amber-400" style={{ fontSize: 18, lineHeight: 1 }}>
                      group
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-amber-300 truncate">
                        Pod ready — tap to join
                      </div>
                      <div className="text-xs text-on-surface-variant truncate">
                        Your matchmaking lobby is confirmed
                      </div>
                    </div>
                    <span className="material-icons text-amber-400 text-[16px]">
                      arrow_forward
                    </span>
                  </button>
                )}

                {!sseConnected && (
                  <div className="flex items-center gap-1.5 px-3 py-2 border-b border-outline-variant text-xs text-on-surface-variant">
                    <span className="material-icons text-[14px]" style={{ color: 'var(--warning-text)' }}>wifi_off</span>
                    <span>Live updates interrupted — reconnecting…</span>
                  </div>
                )}

                {taskList.length === 0 && !podReady ? (
                  <div className="px-3 py-6 text-sm text-on-surface-variant text-center">
                    No active tasks
                  </div>
                ) : (
                  <div className="py-1">
                    {taskList.map(task => {
                      let phaseLabel = task.phase_label;
                      if (task.task_type !== 'system' && task.phase === 'rate_limited' && task.retry_after) {
                        const secsLeft = Math.max(0, Math.ceil(task.retry_after - Date.now() / 1000));
                        phaseLabel = `Rate limited (${secsLeft}s)`;
                      }
                      return (
                        <button
                          key={task.task_id}
                          type="button"
                          onClick={() => {
                            setBellOpen(false);
                            navigate(task.task_type === 'system' ? '/admin?tab=jobs' : taskDestination(task));
                          }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 bg-transparent border-none text-left cursor-pointer hover:bg-white/5 transition-colors duration-100"
                          style={{ opacity: task.is_active ? 1 : 0.55 }}
                        >
                          <span className={task.phase === 'completed' ? 'text-[var(--success-text)]' : task.phase === 'failed' ? 'text-[var(--error-text)]' : 'text-primary'}>
                            {phaseIcon(task.phase, task.task_type === 'system' ? undefined : task.deck_id)}
                          </span>
                          <div className="flex-1 min-w-0">
                            {task.task_type === 'system' ? (
                              <>
                                <div className="text-sm font-medium text-on-surface truncate">
                                  {task.phase_label}
                                </div>
                                <div className="text-xs text-on-surface-variant truncate">
                                  {task.units_total != null
                                    ? `${task.units_done.toLocaleString()} / ${task.units_total.toLocaleString()} ${task.unit}`
                                    : `${task.units_done.toLocaleString()} ${task.unit}`}
                                  {task.message && ` · ${task.message}`}
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="text-sm font-medium text-on-surface truncate">
                                  {task.deck_name}
                                </div>
                                <div className="text-xs text-on-surface-variant truncate">
                                  {phaseLabel}
                                </div>
                              </>
                            )}
                          </div>
                          {task.is_active ? (
                            <div className="w-[60px] h-1 rounded-full overflow-hidden bg-outline-variant shrink-0">
                              <div
                                className="h-full rounded-full bg-primary transition-all"
                                style={{ width: `${task.progress_pct}%` }}
                              />
                            </div>
                          ) : (
                            <span
                              className="material-icons text-[16px]"
                              style={{ color: task.phase === 'completed' ? 'var(--success-text)' : 'var(--error-text)' }}
                            >
                              {task.phase === 'completed' ? 'check_circle' : 'error'}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Log in link for unauthenticated users */}
          {!user && (
            <Link
              to="/login"
              className="nav-text-btn desktop-nav-link text-primary no-underline text-md font-medium py-[6px] px-[12px] rounded-pill"
            >
              Log in
            </Link>
          )}

          {/* User avatar initial */}
          {user && userInitial && (
            <Link
              to={`/user/${user.id}`}
              title={user.display_name || user.email}
              aria-label={user.display_name || 'Your profile'}
              className="w-[32px] h-[32px] rounded-full bg-primary flex items-center justify-center text-md font-bold text-on-primary no-underline shrink-0 ml-[4px]"
            >
              {userInitial}
            </Link>
          )}
        </div>
      </header>

      {/* Scrim */}
      {drawerOpen && (
        <div
          aria-hidden="true"
          onClick={() => setDrawerOpen(false)}
          className="fixed inset-0 z-[1100] bg-black/60"
        />
      )}

      {/* Nav drawer */}
      <nav
        role={drawerOpen ? 'dialog' : undefined}
        aria-modal={drawerOpen ? 'true' : undefined}
        aria-label="Navigation menu"
        className="fixed top-0 left-0 bottom-0 w-[280px] z-[1200] bg-surface-container shadow-[4px_0_12px_rgba(0,0,0,0.3)] transition-transform duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] flex flex-col"
        style={{ transform: drawerOpen ? 'translateX(0)' : 'translateX(-100%)' }}
      >
        <div className="pt-[20px] px-spacing-md pb-[12px]">
          <img
            src="/static/images/meta-command-logo.png"
            alt="MetaCommand"
            className="w-full h-auto object-contain"
          />
        </div>

        {navItems.map((item) => {
          const active = item.path ? location.pathname === item.path : false;
          const cls = `flex items-center gap-[12px] py-[12px] px-spacing-lg text-md font-medium rounded-r-[28px] mr-[12px] no-underline transition-colors duration-150 ${active ? 'text-primary bg-primary/[0.12]' : 'text-on-surface bg-transparent'}`;
          if (item.action) {
            return (
              <button
                key={item.label}
                type="button"
                onClick={item.action}
                className={`${cls} border-none cursor-pointer w-full text-left`}
              >
                {item.label}
              </button>
            );
          }
          return (
            <Link
              key={item.path}
              to={item.path!}
              onClick={() => setDrawerOpen(false)}
              className={cls}
            >
              {item.label}
            </Link>
          );
        })}

        <div className="flex-1" />

        {/* User section */}
        <div className="py-[12px] px-spacing-md border-t border-outline-variant">
          {user ? (
            <div className="flex flex-col gap-2">
              <div className="text-base text-on-surface-variant">
                {user.display_name || user.email}
                {isAdmin(user) && (
                  <span className="badge badge-admin ml-spacing-sm">Admin</span>
                )}
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="bg-transparent border border-outline-variant text-on-surface cursor-pointer py-[6px] px-[12px] rounded-sm text-base text-left"
              >
                Log out
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              onClick={() => setDrawerOpen(false)}
              className="block py-spacing-sm px-[12px] text-md text-primary no-underline"
            >
              Log in / Register
            </Link>
          )}
        </div>
      </nav>

      {/* Dec-a-Sketch drawer — slides in from right */}
      {dasOpen && canSketch && (
        <>
          <div
            aria-hidden="true"
            onClick={() => setDasOpen(false)}
            className="fixed inset-0 z-[1100] bg-black/50"
          />
          <div className="fixed top-0 right-0 bottom-0 z-[1200] w-full max-w-[560px] bg-surface shadow-[-4px_0_24px_rgba(0,0,0,0.4)] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-outline-variant shrink-0">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-primary text-[20px] leading-none">draw</span>
                <span className="font-semibold text-on-surface text-base">Dec-a-Sketch</span>
              </div>
              <button
                type="button"
                onClick={() => setDasOpen(false)}
                className="material-icons bg-transparent border-none text-on-surface-variant cursor-pointer text-[22px] hover:text-on-surface"
              >
                close
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <DecASketchPanel initialRunId={dasRunId} />
            </div>
          </div>
        </>
      )}

      {/* Add Deck Dialog */}
      <AddDeckDialog
        open={showAddDeck}
        onClose={() => setShowAddDeck(false)}
        onDeckAdded={(deckId) => {
          setShowAddDeck(false);
          void queryClient.invalidateQueries({ queryKey: ['decks', 'my'] });
          // Only navigate to my-decks if the dialog didn't already navigate to the deck
          if (!deckId && location.pathname !== '/my-decks') {
            navigate('/my-decks');
          }
        }}
      />
    </>
  );
}
